import React, { createContext, useContext, useEffect, useState } from 'react'
import { MobileContext as MobileContextType } from '../types'

interface MobileProviderProps {
  children: React.ReactNode
}

const MobileContext = createContext<MobileContextType | undefined>(undefined)

export const useMobileContext = () => {
  const context = useContext(MobileContext)
  if (!context) {
    throw new Error('useMobileContext must be used within a MobileProvider')
  }
  return context
}

export const MobileProvider: React.FC<MobileProviderProps> = ({ children }) => {
  const [mobileContext, setMobileContext] = useState<MobileContextType>({
    isMobile: false,
    isTablet: false,
    touchDevice: false,
    screenSize: 'lg',
    orientation: 'portrait'
  })

  const updateMobileContext = () => {
    const width = window.innerWidth
    const height = window.innerHeight
    const userAgent = navigator.userAgent
    
    // Detect mobile devices
    const isMobile = width < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
    
    // Detect tablets
    const isTablet = (width >= 768 && width < 1024) || /iPad/i.test(userAgent)
    
    // Detect touch devices
    const touchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    
    // Determine screen size
    let screenSize: 'sm' | 'md' | 'lg' | 'xl' = 'sm'
    if (width >= 1280) {
      screenSize = 'xl'
    } else if (width >= 1024) {
      screenSize = 'lg'
    } else if (width >= 768) {
      screenSize = 'md'
    }
    
    // Determine orientation
    const orientation = height > width ? 'portrait' : 'landscape'
    
    setMobileContext({
      isMobile,
      isTablet,
      touchDevice,
      screenSize,
      orientation
    })
  }

  useEffect(() => {
    // Initial check
    updateMobileContext()
    
    // Listen for resize events
    window.addEventListener('resize', updateMobileContext)
    window.addEventListener('orientationchange', updateMobileContext)
    
    return () => {
      window.removeEventListener('resize', updateMobileContext)
      window.removeEventListener('orientationchange', updateMobileContext)
    }
  }, [])

  // Add CSS classes to body for styling
  useEffect(() => {
    const { isMobile, isTablet, touchDevice } = mobileContext
    
    const classList = document.body.classList
    
    // Remove existing mobile classes
    classList.remove('is-mobile', 'is-tablet', 'is-touch', 'is-desktop')
    
    // Add appropriate classes
    if (isMobile) {
      classList.add('is-mobile')
    } else if (isTablet) {
      classList.add('is-tablet')
    } else {
      classList.add('is-desktop')
    }
    
    if (touchDevice) {
      classList.add('is-touch')
    }
  }, [mobileContext])

  // Handle viewport height on mobile (for iOS safari)
  useEffect(() => {
    const updateViewportHeight = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    updateViewportHeight()
    window.addEventListener('resize', updateViewportHeight)
    window.addEventListener('orientationchange', () => {
      setTimeout(updateViewportHeight, 100) // Delay to ensure correct height
    })

    return () => {
      window.removeEventListener('resize', updateViewportHeight)
      window.removeEventListener('orientationchange', updateViewportHeight)
    }
  }, [])

  return (
    <MobileContext.Provider value={mobileContext}>
      {children}
    </MobileContext.Provider>
  )
}